import PageHeader from "@/components/PageHeader";
import CourseCard from "@/components/CourseCard";
import CTASection from "@/components/CTASection";
import { COMPETITIVE_EXAMS, FOUNDATION_STAGES } from "@/constants/site";
import SEO, { breadcrumb, course } from "@/components/SEO";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function CompetitiveExams() {
  return (
    <div>
      <SEO
        title="NEET Coaching, JEE Coaching & Foundation Course | AM Group of Education"
        description="Focused NEET preparation, JEE (Main + Advanced) coaching and Foundation Course for Classes 8, 9 and 10 with weekly mocks and expert mentors."
        path="/competitive-exams"
        jsonLd={[
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Competitive Exams", path: "/competitive-exams" },
          ]),
          course("NEET Coaching", "Complete NEET-UG coaching with biology specialists, integrated test series and weekly mocks.", "/competitive-exams"),
          course("JEE Coaching (Main + Advanced)", "IIT-level engineering entrance preparation with problem-solving workshops.", "/competitive-exams"),
        ]}
      />
      <PageHeader
        title="Competitive Exams"
        subtitle="NEET · JEE · Foundation Program — dedicated coaching with weekly mocks, IIT/AIIMS-level problem solving and career-focused mentorship."
        crumb="Competitive Exams"
      />

      {/* NEET / JEE */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">Medical &amp; Engineering</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight">NEET &amp; JEE</h2>
            <p className="mt-3 text-brand-mute">Focused, disciplined coaching from mentors who understand the exam inside out — with small cohorts and weekly performance analytics.</p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {COMPETITIVE_EXAMS.map((c, i) => (
              <CourseCard
                key={c.title}
                title={c.title}
                tag={c.tag}
                desc={c.desc}
                icon={c.icon}
                delay={i * 0.08}
                testId={`competitive-card-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Foundation */}
      <section className="py-16 sm:py-20 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">Foundation Program</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight">Class 8 &middot; 9 &middot; 10 Foundation</h2>
            <p className="mt-3 text-brand-mute">Give your competitive-exam journey a two-year head-start with our integrated foundation program for Classes 8–10.</p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FOUNDATION_STAGES.map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className="rounded-2xl border border-brand-line bg-white p-6 flex flex-col h-full"
                data-testid={`foundation-card-${i}`}
              >
                <span className="w-12 h-12 rounded-xl bg-brand-primary text-brand-gold grid place-items-center">
                  <i className={`fa-solid fa-${f.icon}`} />
                </span>
                <h3 className="mt-4 display-serif text-xl font-bold text-brand-dark">{f.name}</h3>
                <p className="mt-2 text-sm text-brand-mute leading-relaxed flex-1">{f.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to="/contact" data-testid={`foundation-card-${i}-learn`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all">
                    Learn More <i className="fa-solid fa-arrow-right text-[10px]" />
                  </Link>
                  <Link to="/contact" data-testid={`foundation-card-${i}-enquire`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-brand-primary text-white hover:bg-brand-gold hover:text-brand-dark transition-all shadow-md">
                    Enquire Now <i className="fa-solid fa-paper-plane text-[10px]" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
